/*
  S3 Sensitivity Science - Funnel Tracking
  Pushes funnel events to window.dataLayer for Web GTM / sGTM relay.
  Loaded via snippets/s3-tracking.liquid from each tracked section, so it
  travels with the section when copied across pages.
*/
(function () {
  'use strict';

  if (window.__s3TrackingBootstrapped) return;
  window.__s3TrackingBootstrapped = true;

  window.dataLayer = window.dataLayer || [];

  var FUNNEL_SESSION_TIMEOUT_MS = 30 * 60 * 1000;
  var EMAIL_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  /* ---------- Storage-safe helpers ---------- */

  function safeGet(store, key) {
    try { return store.getItem(key); } catch (e) { return null; }
  }
  function safeSet(store, key, value) {
    try { store.setItem(key, value); } catch (e) { /* no-op */ }
  }
  function safeRemove(store, key) {
    try { store.removeItem(key); } catch (e) { /* no-op */ }
  }

  /* ---------- Event ID ---------- */

  function generateEventId() {
    try {
      if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    } catch (e) { /* fall through */ }
    return 'evt_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);
  }

  /* ---------- Funnel session ID (30-min rolling) ---------- */

  function getFunnelSessionId() {
    var stored = safeGet(localStorage, 's3_funnel_session_id');
    var lastActivity = parseInt(safeGet(localStorage, 's3_funnel_session_last_activity') || '0', 10);
    var now = Date.now();
    if (stored && (now - lastActivity) < FUNNEL_SESSION_TIMEOUT_MS) {
      safeSet(localStorage, 's3_funnel_session_last_activity', now.toString());
      return stored;
    }
    var id = 'fs_' + now + '_' + Math.random().toString(36).slice(2, 11);
    safeSet(localStorage, 's3_funnel_session_id', id);
    safeSet(localStorage, 's3_funnel_session_last_activity', now.toString());
    return id;
  }

  /* ---------- Quiz session ID (per quiz attempt) ---------- */

  function getQuizSessionId() {
    var id = safeGet(sessionStorage, 's3_quiz_session_id');
    if (!id) {
      id = 'qs_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);
      safeSet(sessionStorage, 's3_quiz_session_id', id);
    }
    return id;
  }

  /* ---------- Email persistence (cross-page handoff) ---------- */

  function persistEmail(email) {
    if (!email) return;
    safeSet(localStorage, 's3_lead_email', email);
    safeSet(localStorage, 's3_lead_email_created_at', Date.now().toString());
  }

  function getEmail() {
    var stored = safeGet(localStorage, 's3_lead_email');
    var createdAt = parseInt(safeGet(localStorage, 's3_lead_email_created_at') || '0', 10);
    if (stored && createdAt && (Date.now() - createdAt) < EMAIL_TTL_MS) {
      return stored;
    }
    if (stored) {
      safeRemove(localStorage, 's3_lead_email');
      safeRemove(localStorage, 's3_lead_email_created_at');
    }
    return null;
  }

  /* ---------- Entry method (how the user reached the quiz) ---------- */

  function markEntryMethod(method) { safeSet(sessionStorage, 's3_quiz_entry_method', method); }
  function getEntryMethod() {
    var method = safeGet(sessionStorage, 's3_quiz_entry_method');
    if (method) return method;
    if (getEmail()) return 'localStorage';
    return 'direct';
  }

  /* ---------- Cookie helper ---------- */

  function getCookie(name) {
    try {
      var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
      return match ? decodeURIComponent(match[2]) : null;
    } catch (e) { return null; }
  }

  /* ---------- UTM capture + read ---------- */

  function captureUtms() {
    try {
      var params = new URLSearchParams(window.location.search);
      UTM_KEYS.forEach(function (key) {
        var value = params.get(key);
        if (value) safeSet(localStorage, 's3_' + key, value);
      });
    } catch (e) { /* no-op */ }
  }

  function getUtmParam(name) { return safeGet(localStorage, 's3_' + name) || null; }

  function marketingBlock() {
    return {
      utm_source: getUtmParam('utm_source'),
      utm_medium: getUtmParam('utm_medium'),
      utm_campaign: getUtmParam('utm_campaign'),
      utm_content: getUtmParam('utm_content'),
      utm_term: getUtmParam('utm_term'),
      fbc: getCookie('_fbc'),
      fbp: getCookie('_fbp')
    };
  }

  /* ---------- SHA-256 + HMAC ---------- */

  async function sha256(message) {
    if (!message) return null;
    try {
      var buf = new TextEncoder().encode(message);
      var hash = await crypto.subtle.digest('SHA-256', buf);
      return Array.from(new Uint8Array(hash)).map(function (b) { return b.toString(16).padStart(2, '0'); }).join('');
    } catch (e) { return null; }
  }

  async function hmacSha256(message, secret) {
    var enc = new TextEncoder();
    var key = await crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    var sig = await crypto.subtle.sign('HMAC', key, enc.encode(message));
    return Array.from(new Uint8Array(sig)).map(function (b) { return b.toString(16).padStart(2, '0'); }).join('');
  }

  async function validateEmailSignature(email, providedSignature) {
    if (!window.S3_HMAC_SECRET) {
      console.warn('[S3 Tracking] HMAC secret not configured — cannot validate email link');
      return false;
    }
    try {
      var expected = await hmacSha256(email, window.S3_HMAC_SECRET);
      return expected === providedSignature;
    } catch (e) { return false; }
  }

  async function readEmailFromUrl() {
    try {
      var params = new URLSearchParams(window.location.search);
      var encoded = params.get('e');
      var signature = params.get('s');
      if (!encoded || !signature) return null;

      var email;
      try { email = atob(encoded); }
      catch (e) {
        console.warn('[S3 Tracking] Invalid email encoding in URL');
        return null;
      }

      var valid = await validateEmailSignature(email, signature);
      if (!valid) {
        console.warn('[S3 Tracking] Invalid email signature in URL');
        return null;
      }
      return email;
    } catch (e) { return null; }
  }

  /* ---------- ICP mapping (severity-based) ---------- */

  function mapToIcp(severity) {
    var s = (severity || '').toString();
    switch (s) {
      case 'none':
        return { icp: 'oral_health_maximisers', icp_confidence: 'high', bucket: 'none' };
      case 'veryInfrequent':
        return { icp: 'proactive_champions', icp_confidence: 'high', bucket: 'mild' };
      case 'comesAndGoes':
        return { icp: 'disgruntled_sensodyne', icp_confidence: 'high', bucket: 'moderate' };
      case 'smallDaily':
      case 'significant':
        return { icp: 'disgruntled_sensodyne', icp_confidence: 'high', bucket: 'severe' };
      default:
        return { icp: 'unknown', icp_confidence: 'low', bucket: 'unknown' };
    }
  }

  /* ---------- Klaviyo profile update (direct, browser-side) ----------
     Used at quiz_complete to write ICP + classification + severity back to
     the user's Klaviyo profile. Lives client-side because sGTM/GA4 redaction
     strips plaintext email before it reaches a server-side tag, and Klaviyo
     needs plaintext email to match a profile. Fire-and-forget — failures are
     logged but never block the funnel. */

  async function updateKlaviyoProfileWithIcp(email, payload) {
    if (!email) {
      console.warn('[S3 Tracking] Cannot update Klaviyo profile — no email');
      return;
    }
    var companyId = window.S3_KLAVIYO_PUBLIC_KEY;
    if (!companyId) {
      console.warn('[S3 Tracking] Klaviyo public API key not configured — skipping profile update');
      return;
    }
    try {
      await fetch('https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-10-15'
        },
        body: JSON.stringify({
          data: {
            type: 'profile',
            attributes: {
              email: email,
              properties: {
                icp: payload.icp,
                icp_confidence: payload.icp_confidence,
                quiz_classification: payload.classification,
                quiz_severity: payload.severity,
                quiz_severity_bucket: payload.severity_bucket,
                quiz_completed_at: new Date().toISOString()
              }
            }
          }
        })
      });
    } catch (e) {
      console.error('[S3 Tracking] Klaviyo profile update failed:', e && e.message);
    }
  }

  /* ---------- Defensive dataLayer push ---------- */

  function push(payload) {
    try { window.dataLayer.push(payload); }
    catch (e) { console.error('[S3 Tracking] dataLayer push failed', e && e.message); }
  }

  /* ---------- CustomEvent listener: section dispatch → dataLayer ---------- */

  document.addEventListener('s3:lead_cta_click', function (e) {
    var d = (e && e.detail) || {};
    push({
      event: 'lead_cta_click',
      event_id: generateEventId(),
      funnel_session_id: getFunnelSessionId(),
      cta: { label: d.label || '', location: d.location || 'hero' }
    });
  });

  document.addEventListener('s3:lead_modal_open', function () {
    push({
      event: 'lead_modal_open',
      event_id: generateEventId(),
      funnel_session_id: getFunnelSessionId()
    });
  });

  document.addEventListener('s3:lead_modal_dismiss', function (e) {
    var d = (e && e.detail) || {};
    push({
      event: 'lead_modal_dismiss',
      event_id: generateEventId(),
      funnel_session_id: getFunnelSessionId(),
      dismiss: { method: d.method || 'unknown' }
    });
  });

  document.addEventListener('s3:lead_capture', async function (e) {
    var d = (e && e.detail) || {};
    var firstName = (d.firstName || '').trim();
    var lastName = (d.lastName || '').trim();
    var email = (d.email || '').trim();

    if (email) persistEmail(email);

    var normalisedEmail = email.toLowerCase();
    var normalisedFirst = firstName.toLowerCase();
    var normalisedLast = lastName.toLowerCase();

    var hashes = [null, null, null];
    try {
      hashes = await Promise.all([
        sha256(normalisedEmail),
        sha256(normalisedFirst),
        sha256(normalisedLast)
      ]);
    } catch (err) { /* hashing failed, fall through with nulls */ }

    push({
      event: 'lead_capture',
      event_id: generateEventId(),
      funnel_session_id: getFunnelSessionId(),
      user_data: {
        email: email || null,
        first_name: firstName || null,
        last_name: lastName || null,
        email_sha256: hashes[0],
        first_name_sha256: hashes[1],
        last_name_sha256: hashes[2]
      },
      marketing: marketingBlock(),
      value: 0,
      currency: 'GBP'
    });
  });

  document.addEventListener('s3:quiz_start', async function () {
    var email = getEmail();
    var emailHashed = email ? await sha256(email.toLowerCase()) : null;
    push({
      event: 'quiz_start',
      event_id: generateEventId(),
      funnel_session_id: getFunnelSessionId(),
      user_data: { email: email || null, email_sha256: emailHashed },
      quiz: { session_id: getQuizSessionId(), entry_method: getEntryMethod() }
    });
  });

  document.addEventListener('s3:quiz_step', function (e) {
    var d = (e && e.detail) || {};
    var severity = d.severity || null;
    var icp = severity ? mapToIcp(severity) : null;
    push({
      event: 'quiz_step',
      event_id: generateEventId(),
      funnel_session_id: getFunnelSessionId(),
      quiz: {
        session_id: getQuizSessionId(),
        step_number: typeof d.step_number === 'number' ? d.step_number : null,
        step_id: d.step_id || null,
        answer_value: d.answer_value == null ? null : d.answer_value,
        severity: severity,
        severity_bucket: icp ? icp.bucket : null,
        classification: d.classification || null
      }
    });
  });

  document.addEventListener('s3:quiz_complete', async function (e) {
    var d = (e && e.detail) || {};
    var classification = d.classification || null;
    var severity = d.severity || null;
    var email = getEmail();
    var emailHashed = email ? await sha256(email.toLowerCase()) : null;
    var icp = mapToIcp(severity);

    if (!email) {
      console.warn('[S3 Tracking] quiz_complete fired with no persisted email — profile cannot be retroactively tagged');
    }

    push({
      event: 'quiz_complete',
      event_id: generateEventId(),
      funnel_session_id: getFunnelSessionId(),
      user_data: { email: email || null, email_sha256: emailHashed },
      user_classification: { icp: icp.icp, icp_confidence: icp.icp_confidence },
      quiz: {
        session_id: getQuizSessionId(),
        classification: classification,
        severity: severity,
        severity_bucket: icp.bucket
      }
    });

    // Fire-and-forget: write the ICP back to the Klaviyo profile directly.
    // Needed because sGTM/GA4 redaction strips plaintext email before a
    // server-side Klaviyo tag could match the profile.
    if (email) {
      updateKlaviyoProfileWithIcp(email, {
        icp: icp.icp,
        icp_confidence: icp.icp_confidence,
        classification: classification,
        severity: severity,
        severity_bucket: icp.bucket
      });
    }
  });

  /* ---------- Auto-fire landing_page_view (one-shot per page-load) ---------- */

  function fireLandingPageView() {
    if (window.__s3LandingPageViewed) return;
    window.__s3LandingPageViewed = true;
    push({
      event: 'landing_page_view',
      event_id: generateEventId(),
      funnel_session_id: getFunnelSessionId(),
      page: {
        landing_page_slug: window.location.pathname,
        referrer: document.referrer || 'direct'
      },
      marketing: marketingBlock()
    });
  }

  /* ---------- Bootstrap (capture UTMs, recover Klaviyo email, fire landing) ---------- */

  captureUtms();
  getFunnelSessionId();

  (async function () {
    try {
      var urlEmail = await readEmailFromUrl();
      if (urlEmail) {
        persistEmail(urlEmail);
        markEntryMethod('klaviyo_link');
      }
    } catch (e) { /* no-op */ }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fireLandingPageView, { once: true });
    } else {
      fireLandingPageView();
    }
  })();

  /* ---------- Public API (small surface for debugging / niche callers) ---------- */

  window.S3Tracking = {
    persistEmail: persistEmail,
    getEmail: getEmail,
    mapToIcp: mapToIcp,
    sha256: sha256,
    getFunnelSessionId: getFunnelSessionId,
    getQuizSessionId: getQuizSessionId
  };
})();
